import {
  ChevronLeftIcon,
  InformationCircleIcon,
  MagnifyingGlassIcon,
  XMarkIcon
} from '@heroicons/react/24/solid';
import { Dialog, DialogContent } from '@mui/material';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../components/ui/button';
import {
  CountryConfig,
  PIN_LIMIT_COUNTRIES
} from '../../../constants/pin-limit-countries.const';
import { useApp as portalApp } from '../../../context/usePortal';
import {
  useGetPinLimits,
  useUpsertPinLimit
} from '../../../hooks/settings/usePinLimit';
import {
  formatCurrency,
  getCurrencySymbol
} from '../../../utils/fiat-currency.util';
import ClosePopupConfirmationPopup from '../components/ClosePopupConfirmationPopup';
import LoadingPopup from '../components/LoadingPopup';
import PinLimitFailurePopup from '../components/PinLimitFailurePopup';
import PinLimitSuccessPopup from '../components/PinLimitSuccessPopup';
import PINVerificationPopup from '../components/PINVerificationPopup';
import SetLimitDialog from '../components/SetLimitDialog';

type DialogStep = 'limit' | 'pin' | 'success' | 'failed' | 'loading' | null;

const PinLimitPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = portalApp();

  // Per-country configured limits fetched from backend
  const { configuredLimits, isLoading: limitsLoading } = useGetPinLimits();
  const upsertMutation = useUpsertPinLimit();

  const [selectedCountry, setSelectedCountry] = useState<CountryConfig | null>(
    null
  );
  const [dialogStep, setDialogStep] = useState<DialogStep>(null);
  const [stagingLimit, setStagingLimit] = useState<number>(0);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [showCloseConfirm, setShowCloseConfirm] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');

  const filteredCountries = PIN_LIMIT_COUNTRIES.filter(
    (c) =>
      c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.currencyCode.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const openLimitDialog = (country: CountryConfig) => {
    setSelectedCountry(country);
    setStagingLimit(configuredLimits[country.currencyCode] ?? 0);
    setErrorMessage('');
    setDialogStep('limit');
  };

  const handleLimitConfirm = (limit: number) => {
    setStagingLimit(limit);
    setDialogStep('pin');
  };

  const handleVerificationProcessStatusChange = (status: boolean) => {
    if (status) {
      setDialogStep('loading');
    }
  };

  const handleAuthStatusChange = (status: string) => {
    if (status === 'succeed' && selectedCountry) {
      setDialogStep('success');
    } else if (status === 'failed') {
      setErrorMessage(
        'Failed to update the limit due to an incorrect PIN. Please try again.'
      );
      setDialogStep('failed');
    }
  };

  const handleClose = () => {
    setDialogStep(null);
    setSelectedCountry(null);
    setShowCloseConfirm(false);
  };

  const handleDialogClose = () => {
    if (
      dialogStep === 'success' ||
      dialogStep === 'failed' ||
      dialogStep === null
    ) {
      handleClose();
    } else {
      setShowCloseConfirm(true);
    }
  };

  const isDialogOpen = dialogStep !== null;

  if (!user?.has_pin) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-4 text-center">
        <p className="text-gray-700">
          You need to set a PIN before configuring transaction limits.
        </p>
        <Button onClick={() => navigate('/settings')}>Go to Settings</Button>
      </div>
    );
  }

  return (
    <div>
      {/* Header — same design as Settings page */}
      <div className="relative overflow-hidden bg-orange-600 pb-32">
        <div
          aria-hidden="true"
          className="absolute inset-x-0 left-1/2 w-full -translate-x-1/2 transform overflow-hidden lg:inset-y-0"
        >
          <div className="absolute inset-0 flex">
            <div
              className="h-full w-1/2"
              style={{ backgroundColor: '#f77f00' }}
            />
            <div
              className="h-full w-1/2"
              style={{ backgroundColor: '#fd9d01' }}
            />
          </div>
          <div className="relative flex justify-center">
            <svg
              className="flex-shrink-0"
              width={1750}
              height={308}
              viewBox="0 0 1750 308"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M284.161 308H1465.84L875.001 182.413 284.161 308z"
                fill="#f77f00"
              />
              <path
                d="M1465.84 308L16.816 0H1750v308h-284.16z"
                fill="#fd7e14"
              />
              <path d="M1733.19 0L284.161 308H0V0h1733.19z" fill="#fb8c00" />
              <path
                d="M875.001 182.413L1733.19 0H16.816l858.185 182.413z"
                fill="#ff9f00"
              />
            </svg>
          </div>
        </div>
        <header className="relative py-10">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <button
              type="button"
              onClick={() => navigate('/settings')}
              className="mb-2 flex items-center gap-1 text-white/80 hover:text-white"
            >
              <ChevronLeftIcon className="h-4 w-4" />
              <span className="text-sm">Settings</span>
            </button>
            <h1 className="text-3xl font-bold tracking-tight text-white">
              Select Country
            </h1>
            <p className="mt-1 text-sm text-white/80">
              Choose a region to establish transaction boundaries and secure
              your account.
            </p>
          </div>
        </header>
      </div>

      {/* Main content */}
      <main className="relative -mt-32">
        <div className="mx-auto max-w-screen-xl px-4 pb-6 sm:px-6 lg:px-8 lg:pb-16">
          <div className="overflow-hidden rounded-lg bg-white shadow">
            <div className="p-4 sm:p-6">
              {/* Search */}
              <div className="relative mb-4">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search countries..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full rounded-xl border border-gray-200 py-2.5 pl-9 pr-4 text-sm outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400"
                />
              </div>

              {/* Info alert */}
              <div className="mb-4 flex items-start gap-2 rounded-xl bg-blue-50 px-4 py-3">
                <InformationCircleIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-500" />
                <p className="text-sm text-blue-600">
                  If not configured, a PIN will be required for all
                  transactions, regardless of the amount.
                </p>
              </div>

              {/* Country list */}
              <div className="flex flex-col gap-3">
                {limitsLoading
                  ? Array.from({ length: 4 }).map((_, i) => (
                      <div
                        key={i}
                        className="flex items-center justify-between rounded-2xl border border-gray-100 bg-white p-4 shadow-sm"
                      >
                        <div className="flex items-center gap-3">
                          <div className="h-9 w-9 animate-pulse rounded-full bg-gray-200" />
                          <div className="flex flex-col gap-2">
                            <div className="h-3.5 w-40 animate-pulse rounded bg-gray-200" />
                            <div className="h-3 w-24 animate-pulse rounded bg-gray-100" />
                          </div>
                        </div>
                        <div className="h-9 w-24 animate-pulse rounded-lg bg-gray-200" />
                      </div>
                    ))
                  : filteredCountries.map((country) => {
                      const configuredLimit =
                        configuredLimits[country.currencyCode];
                      const isConfigured =
                        configuredLimit !== undefined && configuredLimit > 0;
                      const symbol = getCurrencySymbol(country.currencyCode);

                      return (
                        <div
                          key={country.countryCode}
                          className="flex items-center justify-between rounded-2xl border border-gray-100 bg-white p-4 shadow-sm"
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-3xl leading-none">
                              {country.flag}
                            </span>
                            <div>
                              <p className="text-sm font-semibold text-gray-900">
                                {country.name} – {country.currencyCode} (
                                {symbol})
                              </p>
                              {isConfigured ? (
                                <p className="text-sm font-medium text-orange-500">
                                  Configured:{' '}
                                  {formatCurrency(
                                    configuredLimit,
                                    country.currencyCode
                                  )}
                                </p>
                              ) : (
                                <p className="text-sm text-gray-400">
                                  Not Configured
                                </p>
                              )}
                            </div>
                          </div>
                          <Button
                            type="button"
                            onClick={() => openLimitDialog(country)}
                            className="min-w-24"
                          >
                            {isConfigured ? 'Update' : 'Set Limit'}
                          </Button>
                        </div>
                      );
                    })}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Multi-step dialog */}
      {isDialogOpen && selectedCountry && (
        <Dialog
          open={isDialogOpen}
          onClose={handleDialogClose}
          className="rounded-xl border border-red-300 p-0"
        >
          <DialogContent dividers className="min-w-[200px] max-w-[420px] p-0">
            <button onClick={handleDialogClose} className="ms-auto block">
              <XMarkIcon className="h-6 text-end" />
            </button>

            {dialogStep === 'limit' && (
              <SetLimitDialog
                country={selectedCountry}
                currentLimit={
                  (configuredLimits[selectedCountry.currencyCode] ?? 0) > 0
                    ? configuredLimits[selectedCountry.currencyCode]
                    : null
                }
                onConfirm={handleLimitConfirm}
              />
            )}

            {dialogStep === 'pin' && selectedCountry && (
              <PINVerificationPopup
                handleVerificationProcessStatusChange={
                  handleVerificationProcessStatusChange
                }
                handleAuthStatusChange={handleAuthStatusChange}
                onPinSubmit={async (pin) => {
                  await upsertMutation.mutateAsync({
                    currency_code: selectedCountry.currencyCode,
                    pin_limit: stagingLimit,
                    pin
                  });
                  return { success: true };
                }}
              />
            )}

            {dialogStep === 'loading' && <LoadingPopup />}

            {dialogStep === 'success' && (
              <PinLimitSuccessPopup
                limit={stagingLimit}
                currencyCode={selectedCountry.currencyCode}
                onClose={handleClose}
              />
            )}

            {dialogStep === 'failed' && (
              <PinLimitFailurePopup
                errorMessage={errorMessage}
                onClose={handleClose}
                onRetry={() => {
                  setDialogStep('pin');
                }}
              />
            )}
          </DialogContent>
        </Dialog>
      )}

      <ClosePopupConfirmationPopup
        open={showCloseConfirm}
        onClose={() => setShowCloseConfirm(false)}
        onConfirm={handleClose}
        message="Quit setting transaction limit?"
      />
    </div>
  );
};

export default PinLimitPage;
